import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronDown, Globe, Instagram, Mail, MapPin, Phone, Youtube } from 'lucide-react'

type FooterSection = { title: string; links: { to: string; label: string; external?: boolean }[] }

const sections: FooterSection[] = [
  {
    title: 'Discover',
    links: [
      { to: '/rooms', label: 'Browse rooms' },
      { to: '/manauna-dham', label: 'Manauna Dham guide' },
      { to: '/packages', label: 'Yatra packages' },
      { to: '/services', label: 'Services' },
      { to: '/gallery', label: 'Gallery' },
    ],
  },
  {
    title: 'Company',
    links: [
      { to: '/about', label: 'About us' },
      { to: '/vendors', label: 'Our vendors' },
      { to: '/blogs', label: 'Blogs & videos' },
      { to: '/contact', label: 'Contact' },
      { to: '/vendor/signup', label: 'Become a host' },
    ],
  },
  {
    title: 'Support',
    links: [
      { to: '/faqs', label: 'FAQs' },
      { to: '/refund', label: 'Refund policy' },
      { to: '/terms', label: 'Terms & privacy' },
      { to: 'tel:9457425914', label: '9457425914', external: true },
      { to: 'mailto:info@aonlastay.napps.in', label: 'info@aonlastay.napps.in', external: true },
    ],
  },
]

function FooterAccordion({ section }: { section: FooterSection }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border-b border-gray-200 lg:border-0">
      <button
        type="button"
        className="flex w-full items-center justify-between py-4 lg:hidden text-left"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="text-sm font-semibold text-gray-900">{section.title}</span>
        <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      <h4 className="hidden lg:block text-xs font-bold uppercase tracking-[0.18em] text-gray-500 mb-4">{section.title}</h4>
      <ul className={`space-y-3 pb-4 lg:pb-0 ${open ? 'block' : 'hidden lg:block'}`}>
        {section.links.map((link) => (
          <li key={link.label}>
            {link.external ? (
              <a href={link.to} className="text-sm text-gray-600 hover:text-gray-900 hover:underline underline-offset-4 transition-colors">
                {link.label}
              </a>
            ) : (
              <Link to={link.to} className="text-sm text-gray-600 hover:text-gray-900 hover:underline underline-offset-4 transition-colors">
                {link.label}
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

const Footer = () => {
  return (
    <footer className="bg-[#f7f7f7] border-t border-gray-200/80">
      {/* CTA strip — Airbnb-style top band */}
      <div className="bg-white border-b border-gray-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-10">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="max-w-xl">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary-600 mb-2">Aonla Stay</p>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight">
                Stays crafted for Manauna Dham pilgrims
              </h3>
              <p className="mt-2 text-sm sm:text-base text-gray-600 leading-relaxed">
                Book verified hotels, homestays, and dharamshala-style rooms near Aonla before the rush. Premium hospitality, local trust.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 shrink-0">
              <Link
                to="/rooms?location=Manauna"
                className="inline-flex items-center justify-center px-6 py-3 rounded-xl bg-gray-900 text-white text-sm font-semibold hover:bg-gray-800 transition-colors"
              >
                Find stays near Dham
              </Link>
              <Link
                to="/manauna-dham"
                className="inline-flex items-center justify-center px-6 py-3 rounded-xl border border-gray-300 bg-white text-gray-800 text-sm font-semibold hover:border-gray-400 transition-colors"
              >
                Plan your yatra
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8">
          {/* Brand column */}
          <div className="lg:col-span-4 space-y-5">
            <Link to="/" className="inline-block">
              <img
                src="https://www.aonlastay.napps.in/assets/img/1734781145.png"
                alt="Aonla Stay"
                className="h-9 sm:h-10 w-auto"
              />
            </Link>
            <p className="text-sm text-gray-600 leading-relaxed max-w-sm">
              Search and book hotels in Aonla, Manauna, and Khatu Shyam Jeevan Dham. Curated listings, secure checkout, WhatsApp booking updates.
            </p>
            <div className="space-y-2.5 text-sm text-gray-600">
              <a href="tel:9457425914" className="flex items-center gap-2.5 hover:text-gray-900 transition-colors">
                <Phone className="h-4 w-4 shrink-0" /> 9457425914
              </a>
              <div className="flex items-start gap-2.5">
                <MapPin className="h-4 w-4 shrink-0 mt-0.5" />
                <span>Manona Dham, Aonla, Bareilly, UP 243301</span>
              </div>
            </div>
            <div className="flex items-center gap-2 pt-1">
              {[
                { href: 'https://www.youtube.com/@ManaunadhamBhajan', icon: Youtube, label: 'YouTube' },
                { href: 'https://www.instagram.com/explore/tags/manaunadham/', icon: Instagram, label: 'Instagram' },
                { href: 'mailto:info@aonlastay.napps.in', icon: Mail, label: 'Email' },
              ].map(({ href, icon: Icon, label }) => (
                <a
                  key={label}
                  href={href}
                  target={href.startsWith('http') ? '_blank' : undefined}
                  rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
                  aria-label={label}
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-700 hover:border-gray-900 hover:text-gray-900 transition-colors"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Link columns — accordion on mobile, grid on desktop */}
          <div className="lg:col-span-8 grid grid-cols-1 lg:grid-cols-3 gap-0 lg:gap-8">
            {sections.map((section) => (
              <FooterAccordion key={section.title} section={section} />
            ))}
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-gray-200/80 bg-[#f7f7f7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 text-xs sm:text-sm text-gray-600">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span>© {new Date().getFullYear()} Aonla Online</span>
              <span className="hidden sm:inline text-gray-300">·</span>
              <span>All rights reserved</span>
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <span className="inline-flex items-center gap-1.5 font-medium">
                <Globe className="h-3.5 w-3.5" /> English (IN)
              </span>
              <span className="hidden sm:inline text-gray-300">·</span>
              <span className="font-medium">₹ INR</span>
              <Link to="/terms" className="hover:text-gray-900 hover:underline underline-offset-4">Privacy</Link>
              <Link to="/terms" className="hover:text-gray-900 hover:underline underline-offset-4">Terms</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
